import React, { useState, SyntheticEvent, useEffect, Fragment } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Lock, Mail, MailCheck, UserRoundCheck } from "lucide-react";

import { LogoPackageHoriz } from "../sub/LogoPackage";
import { TextInput } from "../shared/Inputs";

import { useSendPasswordResetEmail, useSignIn, useResetPassword, useSetPassword } from "../../hooks/auth-cax";

export const Auth = () => {
    const { view } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (view !== 'sign-in' && view !== 'forget-password' && view !== 'reset-password' && view !== 'set-password') {
            navigate('/auth/sign-in', { replace: true });
        };

        // eslint-disable-next-line
    }, [view]);

    return (
        <div className="auth__page">
            <div className="auth__content">
                <div className="auth__content__left">
                    <LogoPackageHoriz />
                    {view === 'sign-in' && <SignInView />}
                    {view === 'forget-password' && <ForgotPasswordView />}
                    {view === 'reset-password' && <ResetPasswordView />}
                    {view === 'set-password' && <SetPasswordView />}
                </div>
                <div className="auth__content__right">
                    <img src="/assets/auth_bg.png" alt="" className="auth__content__right__bg_img" />
                </div>
            </div>
        </div>
    );
};

const SignInView = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const { mutate: signIn } = useSignIn();

    const isSubmitDisabled = (): boolean => {
        if (!password?.trim()?.length || !email?.trim()?.length) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing) return;

        signIn({
            payload: {
                email,
                password,
            },
            setProcessing,
            setError,
        });
    };

    const goToForgotPassword = () => {
        if (processing) return;

        navigate('/auth/forget-password');
    };

    return (
        <div className="auth__view__sign_in">
            <p className="text-4xl text-bold color--primary">Welcome!</p>
            <p className="text-lg color--txt-secondary mt-lg">Login in continue your cognitive security journey</p>
            <form className="auth__view__sign_in__form" onSubmit={handleSubmit}>
                <div className="auth__view__sign_in__form__form_group">
                    <label htmlFor="sign_in__email">
                        <p className="text-sm text-medium color--primary">Email Address</p>
                    </label>
                    <TextInput
                        id="sign_in__email"
                        type="email"
                        icon={{ icon: <Mail />, position: 'prefix' }}
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div className="auth__view__sign_in__form__form_group my-lg">
                    <label htmlFor="sign_in__password">
                        <p className="text-sm text-medium color--primary">Password</p>
                    </label>
                    <TextInput
                        id="sign_in__password"
                        type="password"
                        icon={{ icon: <Lock />, position: 'prefix' }}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </div>
                <div className="auth__view__sign_in__form__forgot_password_btn__container">
                    <button
                        type="button"
                        className="link link--primary"
                        onClick={goToForgotPassword}
                    >
                        Forgot password?
                    </button>
                </div>
                <button
                    type="submit"
                    className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                    disabled={isSubmitDisabled()}
                >
                    {processing ? <span className="btn-loader" /> : "Sign In"}
                </button>
                {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
            </form>
        </div>
    );
};

const ForgotPasswordView = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);

    const { mutate: sendPasswordResetEmail } = useSendPasswordResetEmail();

    const goToSignIn = () => {
        if (processing) return;
        navigate('/auth/sign-in');
    };

    const isSubmitDisabled = (): boolean => {
        if (!email?.trim()?.length) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing) return;

        sendPasswordResetEmail({
            payload: { email },
            setProcessing,
            setError,
            setSuccess,
        });
    };

    return (
        <div className="auth__view__forgot_password">
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Reset Password</p>
                    <p className="text-lg color--txt-secondary mt-lg">Enter your email and we'll send you reset instructions</p>
                    <form className="auth__view__forgot_password__form" onSubmit={handleSubmit}>
                        <div className="auth__view__forgot_password__form__form_group">
                            <label htmlFor="forgot_password__email">
                                <p className="text-sm text-medium color--primary">Email Address</p>
                            </label>
                            <TextInput
                                id="forgot_password__email"
                                type="email"
                                icon={{ icon: <Mail />, position: 'prefix' }}
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Send Reset Link"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                    <p className="text-center color--txt-tertiary mt-lg">
                        Sign In instead?&nbsp;
                        <button className="link link--primary" onClick={goToSignIn}>Back to Sign in</button>
                    </p>
                </Fragment>
                : <div className="auth__view__forgot_password__sent_reset_link">
                    <div className="auth__view__forgot_password__sent_reset_link__svg_cont">
                        <MailCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Password Reset Link Sent</p>
                    <p className="text-center color--txt-secondary mb-md">
                        We have emailed you the password reset link. Please check your inbox to reset your password.
                    </p>
                    <p className="color--error text-xs mb-xl">
                        Please check the spam folder in case you don't see the email in inbox.
                    </p>
                    <button className="btn btn--md btn--radius-sm btn--primary" onClick={goToSignIn}>
                        Go to Sign In
                    </button>
                </div>
            }
        </div>
    );
};

const ResetPasswordView = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("reset_token");

    const [password, setPassword] = useState('');
    const [confPassword, setConfPassword] = useState('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(5);

    const { mutate: resetPassword } = useResetPassword();

    const isSubmitDisabled = (): boolean => {
        if (!password?.trim()?.length && !confPassword?.trim()?.length) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing || !token) return;

        if (password !== confPassword) {
            setError("Passwords are not matching.");
            return;
        };

        resetPassword({
            payload: {
                password,
            },
            token,
            setProcessing,
            setError,
            setSuccess,
        });
    };

    useEffect(() => {
        if (!success) return;

        const interval = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    useEffect(() => {
        if (countdown > 0) return;
        navigate('/auth/sign-in', { replace: true });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown]);

    return (
        <div className='auth__view__reset_password'>
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Reset Your Password</p>
                    <p className="text-lg color--txt-secondary mt-lg">Create a strong password to access your account</p>
                    <form className="auth__view__reset_password__form" onSubmit={handleSubmit}>
                        <div className="auth__view__reset_password__form__form_group">
                            <label htmlFor="password">
                                <p className="text-sm text-medium color--primary">Password</p>
                            </label>
                            <TextInput
                                id="password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Create password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div className="auth__view__reset_password__form__form_group my-lg">
                            <label htmlFor="conf_password">
                                <p className="text-sm text-medium color--primary">Confirm Password</p>
                            </label>
                            <TextInput
                                id="conf_password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Retype password"
                                value={confPassword}
                                onChange={(e) => setConfPassword(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Reset Password"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                </Fragment>
                : <div className="auth__view__reset_password__success">
                    <div className="auth__view__reset_password__success__svg_cont">
                        <UserRoundCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Password Reset Successful</p>
                    <p className="text-center color--txt-tertiary mb-md">
                        Your password has been successfully updated. Please use your new password to Sign In.
                    </p>
                    <p className="text-lg text-center color--txt-tertiary text-medium">
                        You will be redirected to sign in page in <strong>{countdown}</strong> second{countdown > 1 ? 's' : ''}
                    </p>
                </div>
            }
        </div>
    );
};

const SetPasswordView = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("sp_token");

    const [password, setPassword] = useState('');
    const [confPassword, setConfPassword] = useState('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(5);

    const { mutate: setPasswordMutation } = useSetPassword();

    const isSubmitDisabled = (): boolean => {
        if (!password?.trim()?.length && !confPassword?.trim()?.length) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing || !token) return;

        if (password !== confPassword) {
            setError("Passwords are not matching.");
            return;
        };

        setPasswordMutation({
            payload: {
                password,
            },
            token,
            setProcessing,
            setError,
            setSuccess,
        });
    };

    useEffect(() => {
        if (!success) return;

        const interval = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    useEffect(() => {
        if (countdown > 0) return;
        navigate('/auth/sign-in', { replace: true });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown]);

    return (
        <div className='auth__view__set_password'>
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Set Your Password</p>
                    <p className="text-lg color--txt-secondary mt-lg">Set a strong password to access your account</p>
                    <form className="auth__view__set_password__form" onSubmit={handleSubmit}>
                        <div className="auth__view__set_password__form__form_group">
                            <label htmlFor="password">
                                <p className="text-sm text-medium color--primary">Password</p>
                            </label>
                            <TextInput
                                id="password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Create password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div className="auth__view__set_password__form__form_group my-lg">
                            <label htmlFor="conf_password">
                                <p className="text-sm text-medium color--primary">Confirm Password</p>
                            </label>
                            <TextInput
                                id="conf_password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Retype password"
                                value={confPassword}
                                onChange={(e) => setConfPassword(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Set Password"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                </Fragment>
                : <div className="auth__view__set_password__success">
                    <div className="auth__view__set_password__success__svg_cont">
                        <UserRoundCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Password Set Successful</p>
                    <p className="text-center color--txt-tertiary mb-md">
                        Your password has been successfully set. Please use your new password to Sign In.
                    </p>
                    <p className="text-lg text-center color--txt-tertiary text-medium">
                        You will be redirected to sign in page in <strong>{countdown}</strong> second{countdown > 1 ? 's' : ''}
                    </p>
                </div>
            }
        </div>
    );
};